<template>
  <section class="about-section" id="about">
    <div class="section-container">
      <div class="section-header">
        <h2 class="section-title">About The Players League</h2>
        <p class="section-subtitle">Premier BCAPL sanctioned pool league serving Dallas-Fort Worth</p>
      </div>
      
      <div class="content-grid">
        <!-- Main Story Card -->
        <div class="story-card main-card">
          <div class="card-header">
            <Icon name="mdi:trophy-award" />
            <h3>Our Story</h3>
          </div>
          <div class="card-content">
            <p>Welcome to The Players League DFW, a premier BCAPL sanctioned Ca$h league serving the Dallas-Fort Worth area. Established in 2025, we provide competitive and enjoyable pool league experiences for players of all skill levels through our exciting Mosconi Format Team Play.</p>
          </div>
        </div>

        <!-- Mission Card -->
        <div class="story-card">
          <div class="card-header">
            <Icon name="mdi:target" />
            <h3>Our Mission</h3>
          </div>
          <div class="card-content">
            <p>To create a welcoming, competitive environment where pool players can improve their skills, build lasting friendships, and enjoy the strategic elements of team-based billiards competition. We believe in fair play, sportsmanship, and fostering a strong sense of community among our members.</p>
          </div>
        </div>

        <!-- What Makes Us Different -->
        <div class="story-card features-card">
          <div class="card-header">
            <Icon name="mdi:star-circle" />
            <h3>What Makes Us Different</h3>
          </div>
          <div class="card-content">
            <div class="features-grid">
              <div class="feature-item">
                <Icon name="mdi:certificate" />
                <div class="feature-content">
                  <h4>BCAPL Sanctioned</h4>
                  <p>Official league with standardized rules ensuring fair competition</p>
                </div>
              </div>
              
              <div class="feature-item">
                <Icon name="mdi:account-group" />
                <div class="feature-content">
                  <h4>Mosconi Format</h4>
                  <p>Exciting 3x3 and 4x team play structures</p>
                </div>
              </div>
              
              <div class="feature-item">
                <Icon name="mdi:format-list-bulleted" />
                <div class="feature-content">
                  <h4>Multiple Formats</h4>
                  <p>Singles and Scotch Doubles for variety</p>
                </div>
              </div>
              
              <div class="feature-item">
                <Icon name="mdi:chart-line" />
                <div class="feature-content">
                  <h4>Professional Scoring</h4>
                  <p>BCAPL Scoring app for accurate tracking</p>
                </div>
              </div>
              
              <div class="feature-item">
                <Icon name="mdi:clock-outline" />
                <div class="feature-content">
                  <h4>Strategic Timeouts</h4>
                  <p>Coaching opportunities that add tactical depth</p>
                </div>
              </div>
            </div>
            
            <div class="info-link">
              <NuxtLink to="/league-format" class="format-link">
                <Icon name="mdi:information" />
                <span>Learn More About Our Format</span>
                <Icon name="mdi:arrow-right" />
              </NuxtLink>
            </div>
          </div>
        </div>

        <!-- Get Involved Card -->
        <div class="story-card cta-card">
          <div class="card-header">
            <Icon name="mdi:account-plus" />
            <h3>Get Involved</h3>
          </div>
          <div class="card-content">
            <p>Whether you're a seasoned pro or new to competitive pool, The Players League DFW welcomes players who are passionate about the game. Join us for an experience that combines skill, strategy, and camaraderie in the heart of North Texas.</p>
            
            <div class="cta-buttons">
              <NuxtLink to="/contact" class="cta-button primary">
                <Icon name="mdi:email" />
                <span>Contact Us</span>
              </NuxtLink>
              <NuxtLink to="/standings" class="cta-button secondary">
                <Icon name="mdi:chart-line" />
                <span>View Standings</span>
              </NuxtLink>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
// No script needed for this static component yet.
// Future enhancements could include dynamic content fetching if needed.
</script>

<style scoped>
.about-section {
  padding: 80px 0;
  background: 
    linear-gradient(180deg, rgba(15, 23, 42, 0.9) 0%, rgba(30, 58, 138, 0.9) 100%),
    url('/images/billiards-2-1920w.webp');
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  position: relative;
  overflow: hidden;
}

.about-section::before {
  content: '';
  position: absolute;
  inset: 0;
  background: 
    radial-gradient(circle at 30% 20%, rgba(255, 184, 28, 0.08) 0%, transparent 50%),
    radial-gradient(circle at 70% 80%, rgba(59, 130, 246, 0.08) 0%, transparent 50%);
  z-index: 1;
}

.section-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 24px;
  position: relative;
  z-index: 2;
}

.section-header {
  text-align: center;
  margin-bottom: 64px;
}

.section-title {
  color: var(--gold);
  font-size: 3rem;
  font-family: var(--font-heading);
  font-weight: 700;
  margin-bottom: 16px;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
  position: relative;
}

.section-title::after {
  content: '';
  position: absolute;
  bottom: -12px;
  left: 50%;
  transform: translateX(-50%);
  width: 80px;
  height: 3px;
  background: linear-gradient(90deg, transparent, var(--gold), transparent);
  border-radius: 2px;
}

.section-subtitle {
  color: var(--cream);
  font-size: 1.2rem;
  opacity: 0.9;
  margin-top: 24px;
  line-height: 1.6;
}

/* Content Grid */
.content-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 32px;
}

/* Story Cards */
.story-card {
  background: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(15px);
  border-radius: 20px;
  border: 1px solid rgba(255, 184, 28, 0.2);
  overflow: hidden;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
}

.story-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(90deg, transparent, var(--gold), transparent);
  opacity: 0;
  transition: opacity 0.4s ease;
}

.story-card:hover {
  transform: translateY(-8px);
  border-color: rgba(255, 184, 28, 0.4);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
}

.story-card:hover::before {
  opacity: 1;
}

.main-card {
  grid-column: 1 / -1;
}

/* Card Headers */
.card-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 24px 32px 16px;
  border-bottom: 1px solid rgba(255, 184, 28, 0.1);
}

.card-header svg {
  font-size: 2rem;
  color: var(--gold);
  flex-shrink: 0;
}

.card-header h3 {
  color: var(--gold);
  font-size: 1.6rem;
  font-family: var(--font-heading);
  font-weight: 600;
  margin: 0;
}

/* Card Content */
.card-content {
  padding: 24px 32px 32px;
}

.card-content p {
  color: var(--cream);
  line-height: 1.7;
  margin-bottom: 20px;
  font-size: 1rem;
}

.card-content p:last-child {
  margin-bottom: 0;
}

/* Features Grid */
.features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 24px;
  margin-bottom: 32px;
}

.feature-item {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 20px;
  background: rgba(255, 184, 28, 0.05);
  border-radius: 12px;
  border: 1px solid rgba(255, 184, 28, 0.1);
  transition: all 0.3s ease;
}

.feature-item:hover {
  background: rgba(255, 184, 28, 0.08);
  border-color: rgba(255, 184, 28, 0.2);
  transform: translateY(-2px);
}

.feature-item svg {
  font-size: 1.8rem;
  color: var(--gold);
  flex-shrink: 0;
  margin-top: 2px;
}

.feature-content h4 {
  color: var(--gold);
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 8px;
}

.feature-content p {
  color: var(--cream);
  font-size: 0.95rem;
  line-height: 1.6;
  margin: 0;
  opacity: 0.9;
}

/* Info Link */
.info-link {
  padding-top: 24px;
  border-top: 1px solid rgba(255, 184, 28, 0.1);
}

.format-link {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 12px 20px;
  background: linear-gradient(135deg, rgba(255, 184, 28, 0.1), rgba(255, 184, 28, 0.05));
  border: 1px solid rgba(255, 184, 28, 0.2);
  border-radius: 10px;
  color: var(--gold);
  text-decoration: none;
  font-weight: 500;
  transition: all 0.3s ease;
}

.format-link:hover {
  background: linear-gradient(135deg, rgba(255, 184, 28, 0.2), rgba(255, 184, 28, 0.1));
  border-color: var(--gold);
  transform: translateY(-2px);
}

/* CTA Buttons */
.cta-buttons {
  display: flex;
  gap: 16px;
  margin-top: 24px;
}

.cta-button {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 14px 24px;
  border-radius: 10px;
  text-decoration: none;
  font-weight: 600;
  font-size: 1rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  flex: 1;
  justify-content: center;
}

.cta-button.primary {
  background: linear-gradient(135deg, var(--gold) 0%, var(--light-gold) 100%);
  color: var(--navy-blue);
  box-shadow: 0 4px 12px rgba(255, 184, 28, 0.3);
}

.cta-button.primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(255, 184, 28, 0.4);
}

.cta-button.secondary {
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 184, 28, 0.3);
  color: var(--gold);
  backdrop-filter: blur(5px);
}

.cta-button.secondary:hover {
  background: rgba(255, 184, 28, 0.1);
  border-color: var(--gold);
  transform: translateY(-2px);
}

/* Responsive Design */
@media (max-width: 1200px) {
  .content-grid {
    grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
    gap: 28px;
  }
}

@media (max-width: 768px) {
  .about-section {
    padding: 60px 0;
  }
  
  .section-container {
    padding: 0 16px;
  }
  
  .section-header {
    margin-bottom: 48px;
  }
  
  .section-title {
    font-size: 2.2rem;
  }
  
  .content-grid {
    grid-template-columns: 1fr;
    gap: 24px;
  }
  
  .features-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }
  
  .card-header,
  .card-content {
    padding-left: 20px;
    padding-right: 20px;
  }
  
  .cta-buttons {
    flex-direction: column;
    gap: 12px;
  }
  
  .feature-item {
    padding: 16px;
  }
}

@media (max-width: 480px) {
  .section-title {
    font-size: 1.8rem;
  }
  
  .section-subtitle {
    font-size: 1.1rem;
  }
  
  .card-header h3 {
    font-size: 1.4rem;
  }
  
  .card-content p {
    font-size: 0.95rem;
  }
}
</style>
